import { SaveBar } from '@shopify/app-bridge-react';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FieldError, FieldErrors, SubmitErrorHandler, UseFormReturn } from 'react-hook-form';
import { isNullOrUndefined } from 'utils/utils';

type SumbmitFn<T> = (data: T) => void | Promise<void>;

/**
 * This hook manages the Shopify contextual save bar for the form passed to it as the first parameter.
 * @param {UseFormReturn<T>} form The form object returned by useForm
 * @param {SumbmitFn<T>} onSubmit The function used to submit the form
 * @param {string} id A unique ID for the contextual save bar
 * @param {boolean} resetOnSubmit Whether to reset the form with the submitted data on successful submit or not.
 * @param {VoidFunction} onReset The function to call when the form is reset.
 * @returns {UseContextualSaveBarReturn} The JSX markup for the save bar, and a "submitForm" function.
 * If there is a regular save button alongside the save bar, that button should call this function when clicked.
 * */
export default function useContextualSaveBar<T>(
    form: UseFormReturn<T>,
    onSubmit: SumbmitFn<T>,
    id: string,
    resetOnSubmit: boolean = true,
    onReset: VoidFunction = () => {}
): UseContextualSaveBarReturn {
    const barRef = useRef<UISaveBarElement>(null);

    const submitAndReset = useCallback(
        (data: T) => {
            onSubmit(data);
            if (resetOnSubmit) {
                form.reset(data);
            }
        },
        [onSubmit, form.reset, resetOnSubmit]
    );

    const _unwrapErrorsByRefRecurse = useCallback((memo: FieldError[], errors: FieldErrors): FieldError[] => {
        for (const value of Object.values(errors)) {
            if ('ref' in value) {
                memo?.push(value as FieldError);
                continue;
            }
            _unwrapErrorsByRefRecurse(memo, value as FieldErrors);
        }
        return memo;
    }, []);

    const unwrapErrorsByRef = useCallback(
        (errors: FieldErrors): FieldError[] => {
            return _unwrapErrorsByRefRecurse([], errors);
        },
        [_unwrapErrorsByRefRecurse]
    );

    const handleRejected: SubmitErrorHandler<T> = useCallback(
        (errors: FieldErrors<T>) => {
            const _errors = unwrapErrorsByRef(errors);
            console.log('Unwrapped errors: ', _errors);

            if (_errors?.[0]?.ref?.name) {
                const _element =
                    document?.getElementsByName(_errors[0].ref.name)?.[0] ||
                    document?.getElementById(_errors[0].ref.name) ||
                    undefined;
                _element?.scrollIntoView({ behavior: 'smooth' });
            }
        },
        [unwrapErrorsByRef]
    );

    const submitForm = useCallback(() => {
        if (isNullOrUndefined(id)) return;

        form.handleSubmit(submitAndReset, handleRejected)().then(() => {
            if (
                barRef?.current?.showing &&
                !form.formState.isDirty &&
                (!form.formState.dirtyFields || isEmpty(form.formState.dirtyFields))
            ) {
                shopify.saveBar.hide(id);
            }
        });
    }, [form.handleSubmit, onSubmit, barRef, form.formState.isDirty, id]);

    const resetForm = useCallback(() => {
        if (isNullOrUndefined(id)) return;
        form.reset();
        if (barRef?.current?.showing) {
            shopify.saveBar.hide(id);
        }
        onReset?.();
    }, [form.reset, barRef, id, onReset]);

    useEffect(() => {
        history.pushState(null, window.location.hostname, window.location.pathname);

        function preventPopState() {
            history.pushState(null, window.location.hostname, window.location.pathname);
            history.go(1);
        }

        window.addEventListener('popstate', preventPopState);

        return () => {
            window.removeEventListener('popstate', preventPopState);
        };
    }, []);

    useEffect((): void => {
        if (isNullOrUndefined(id)) return;

        const dirtyFields = form.formState.dirtyFields;

        if (!form.formState.isDirty || isEmpty(dirtyFields)) {
            shopify.saveBar.hide(id).catch(() => {});
        } else {
            shopify.saveBar.show(id);
        }
    }, [form.formState, id]);

    const saveBar = useMemo(
        () => (
            <SaveBar id={id} ref={barRef}>
                {/* eslint-disable react/no-unknown-property */}
                <button variant="primary" onClick={() => submitForm()}></button>
                <button onClick={resetForm}></button>
            </SaveBar>
        ),
        [id, submitForm, resetForm]
    );

    return { saveBar, submitForm };
}

export interface UseContextualSaveBarReturn {
    saveBar: React.JSX.Element;
    submitForm: () => void;
}
